import { ContractError } from './contracts';

export type Referral = Readonly<{
  playerId: number;
  username: string;
  level: number;
  rewardPaid: boolean;
}>;

export type ReferralPage = Readonly<{
  referrals: readonly Referral[];
  hasMore: boolean;
  nextBeforeId: number;
}>;

export type ReferralGateway = (beforeId: number, signal: AbortSignal) => Promise<ReferralPage>;

export function parseReferrals(value: unknown): ReferralPage {
  const invalid = (): never => {
    throw new ContractError('Invalid referral response.');
  };
  const object = (value: unknown): Record<string, unknown> => {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) return invalid();
    return value as Record<string, unknown>;
  };
  const integer = (value: unknown, minimum: number): number => {
    if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < minimum)
      return invalid();
    return value;
  };
  const body = object(value);
  if (
    body.protocolVersion !== '2' ||
    !Array.isArray(body.referrals) ||
    body.referrals.length > 100 ||
    typeof body.hasMore !== 'boolean'
  )
    return invalid();
  let previousId = Infinity;
  const referrals = body.referrals.map((value): Referral => {
    const row = object(value);
    const playerId = integer(row.playerId, 1);
    const level = integer(row.level, 1);
    if (
      playerId >= previousId ||
      level > 600 ||
      typeof row.username !== 'string' ||
      row.username.length === 0 ||
      typeof row.rewardPaid !== 'boolean'
    )
      return invalid();
    previousId = playerId;
    return { playerId, username: row.username, level, rewardPaid: row.rewardPaid };
  });
  const nextBeforeId = integer(body.nextBeforeId, 0);
  if (
    body.hasMore
      ? referrals.length === 0 || nextBeforeId !== referrals.at(-1)?.playerId
      : nextBeforeId !== 0
  )
    return invalid();
  return { referrals, hasMore: body.hasMore, nextBeforeId };
}
